import React, { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BASE_URL = '/api';

// ─── Falcon logo icon ─────────────────────────────────────────────────────────
// const FalconIcon = () => (
//   <img
//     src="https://tickets.falconsystem.com/assets/images/favicon.ico"
//     alt="Falcon"
//     width="52"
//     height="52"
//     style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
//   />
// );

const Login: React.FC = () => {
  const { login }  = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const from       = (location.state as { from?: string })?.from ?? '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username || !password) { setError('Please enter your username and password.'); return; }
    setError('');
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username, password }),
      });
      const data = await res.json() as { token?: string; user?: { id: string; username: string; role: string }; error?: string };
      if (!res.ok) { setError(data.error ?? 'Sign-in failed. Please check your credentials.'); return; }
      login(data.token!, data.user!);
      navigate(from, { replace: true });
    } catch {
      setError('Unable to connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#F3F2F1',
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
    }}>

      {/* ── Left branding panel ── */}
      <div style={{
        width: '42%',
        minWidth: 360,
        background: '#0078D4',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background pattern */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle at 20% 80%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

        {/* Top — brand */}
        <div style={{ position: 'relative' }}>
          <img
            src="https://tickets.falconsystem.com/assets/images/favicon.ico"
            alt="Falcon"
            width="32"
            height="32"
            style={{ objectFit: 'contain', marginBottom: '20px', display: 'block', filter: 'brightness(0) invert(1)' }}
          />
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500, letterSpacing: '.02em' }}>
            Falcon Systems
          </div>
        </div>

        {/* Middle — headline */}
        <div style={{ position: 'relative' }}>
          {/* <div style={{ marginBottom: '24px' }}>
            <FalconIcon />
          </div> */}
          <h1 style={{
            color: '#FFFFFF',
            fontSize: '32px',
            fontWeight: 700,
            lineHeight: 1.2,
            margin: '0 0 12px',
            letterSpacing: '-0.02em',
          }}>
            Falcon Email<br/>Extractor
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: '14px', lineHeight: 1.7, margin: 0, maxWidth: 320 }}>
            Automated email processing for insurance workflows. AI-powered extraction, real-time notifications, and seamless AMS integration.
          </p>
        </div>

        {/* Bottom — features */}
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { icon: '📧', text: 'Microsoft 365 mailbox monitoring' },
            { icon: '🤖', text: 'GPT-4o AI analysis & extraction' },
            { icon: '⚡', text: 'Real-time webhook notifications' },
          ].map(({ icon, text }) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px', lineHeight: 1 }}>{icon}</span>
              <span style={{ color: 'rgba(255,255,255,0.72)', fontSize: '12.5px' }}>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right sign-in panel ── */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 40px',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>

          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{
              margin: '0 0 6px',
              fontSize: '24px',
              fontWeight: 600,
              color: '#201F1E',
              letterSpacing: '-0.01em',
            }}>
              Sign in
            </h2>
            <p style={{ margin: 0, fontSize: '13.5px', color: '#605E5C' }}>
              Use your Falcon account credentials
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '10px 14px',
              marginBottom: '20px',
              background: '#FDE7E9',
              border: '1px solid #F9BDBD',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#A4262C',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>

            {/* Username */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: '#201F1E',
                marginBottom: '6px',
              }}>
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                placeholder="Enter your username"
                style={{
                  width: '100%',
                  height: '36px',
                  padding: '0 10px',
                  border: `1px solid ${error ? '#F9BDBD' : '#8A8886'}`,
                  borderRadius: '2px',
                  fontSize: '14px',
                  color: '#201F1E',
                  background: '#FFFFFF',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color .1s, box-shadow .1s',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0078D4';
                  e.target.style.boxShadow = '0 0 0 1px #0078D4';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? '#F9BDBD' : '#8A8886';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: '#201F1E',
                marginBottom: '6px',
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  height: '36px',
                  padding: '0 10px',
                  border: `1px solid ${error ? '#F9BDBD' : '#8A8886'}`,
                  borderRadius: '2px',
                  fontSize: '14px',
                  color: '#201F1E',
                  background: '#FFFFFF',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color .1s, box-shadow .1s',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0078D4';
                  e.target.style.boxShadow = '0 0 0 1px #0078D4';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? '#F9BDBD' : '#8A8886';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: '36px',
                background: loading ? '#106EBE' : '#0078D4',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '2px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'background .1s',
                fontFamily: 'inherit',
                letterSpacing: '.01em',
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#106EBE'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#0078D4'; }}
            >
              {loading ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: 14, height: 14,
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                  Signing in…
                </>
              ) : 'Sign in'}
            </button>
          </form>

          {/* Footer */}
          <p style={{
            marginTop: '32px',
            fontSize: '11.5px',
            color: '#8A8886',
            textAlign: 'center',
            lineHeight: 1.6,
          }}>
            Access is restricted to authorized personnel only.<br/>
            Your session is secured with JWT authentication.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
